import { useAuth } from "../contexts/auth-context"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Alert, AlertDescription } from "../components/ui/alert"
import { 
  Shield, 
  AlertTriangle, 
  Mail, 
  Phone, 
  Users, 
  Settings,
  HelpCircle,
  ExternalLink
} from "lucide-react"
import { useNavigate } from "react-router-dom"

export default function GuestDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Welcome, {user?.firstName || 'Guest'}</h1>
          <p className="text-gray-600">Limited Access Dashboard</p>
        </div>


        {/* Available Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Account Management */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                My Account
              </CardTitle>
              <CardDescription>
                View and manage your account information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => navigate('/my-account')}
                className="w-full"
              >
                View Account Details
              </Button>
            </CardContent>
          </Card>

          {/* Contact Support */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5 text-green-600" />
                Get Help
              </CardTitle>
              <CardDescription>
                Contact support or your administrator
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => window.open('mailto:support@urutiiq.com', '_blank')}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Support
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => window.open('tel:+1234567890', '_blank')}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Support
              </Button>
            </CardContent>
          </Card>

          {/* Request Access */}
          <Card className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-purple-600" />
                Request Access
              </CardTitle>
              <CardDescription>
                Request full access to UrutiIQ features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                className="w-full"
                onClick={() => {
                  const subject = encodeURIComponent('Access Request - UrutiIQ Account')
                  const body = encodeURIComponent(`Hello,

I am currently using a guest account and would like to request full access to UrutiIQ features.

Account Details:
- Email: ${user?.email}
- Name: ${user?.firstName} ${user?.lastName}
- Company: ${user?.companyName}

Please upgrade my account to access the full dashboard and features.

Thank you!`)
                  window.open(`mailto:admin@urutiiq.com?subject=${subject}&body=${body}`, '_blank')
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Request Upgrade
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Feature Preview */}
        <Card>
          <CardHeader>
            <CardTitle>What You'll Get with Full Access</CardTitle>
            <CardDescription>
              Unlock these powerful features when your account is upgraded
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Financial Management</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Complete accounting system</li>
                  <li>• Financial reports and analytics</li>
                  <li>• Bank reconciliation</li>
                  <li>• Journal entries and ledgers</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Business Operations</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Inventory management</li>
                  <li>• Point of sale system</li>
                  <li>• Purchase orders and invoicing</li>
                  <li>• Expense tracking</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Advanced Features</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• AI-powered insights</li>
                  <li>• Multi-company management</li>
                  <li>• Custom reporting</li>
                  <li>• Workflow automation</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900">Collaboration</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Team member access</li>
                  <li>• Role-based permissions</li>
                  <li>• Document sharing</li>
                  <li>• Approval workflows</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}